import { FEEDBACK_COLUMNS } from '@/types/filterKeyConst';
import { STRUCTURED_FILTER_OPERATOR } from '@/types/structuredFilterConst';
import { TOPIC_2_TYPE } from '@/types/taxonomyConst';

import type { CandidateItemT } from '../AiTopic/types';

// CSV Row 타입 정의
export type AiTopicCsvRow = {
  title: string;
  prompt: string;
  include: string;
  exclude: string;
};

export type RuleTopicCsvRow = {
  title: string;
  include: string;
  exclude: string;
};

/**
 * 쉼표로 구분된 키워드 문자열을 배열로 변환
 * "a, b, c" -> ["a", "b", "c"]
 */
export function parseKeywords(value: string | undefined): string[] {
  if (!value) return [];
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * include/exclude 키워드로 필터 생성
 * AI 토픽은 SNIPPET 필드, Rule 토픽은 FEEDBACK_TEXT 필드 사용
 */
function buildKeywordFilter(
  include: string,
  exclude: string,
  field: typeof FEEDBACK_COLUMNS.SNIPPET | typeof FEEDBACK_COLUMNS.FEEDBACK_TEXT,
): StructuredFilter | undefined {
  const includeKeywords = parseKeywords(include);
  const excludeKeywords = parseKeywords(exclude);

  if (includeKeywords.length === 0 && excludeKeywords.length === 0) {
    return undefined;
  }

  const filters: StructuredFilterStringNumberValueSet[] = [];
  if (includeKeywords.length > 0) {
    filters.push({
      op: STRUCTURED_FILTER_OPERATOR.OR,
      values: includeKeywords,
    });
  }
  if (excludeKeywords.length > 0) {
    filters.push({
      op: STRUCTURED_FILTER_OPERATOR.NOR,
      values: excludeKeywords,
    });
  }

  return { [field]: filters };
}

/**
 * AI Topic CSV Row를 CandidateItemT로 변환
 * AI 토픽은 SNIPPET 필드를 사용 (AITopicCandidateItem과 동일)
 */
export function parseAiTopicCsv(rows: AiTopicCsvRow[]): CandidateItemT[] {
  return rows
    .filter((row) => row.title?.trim())
    .map((row) => ({
      name: row.title.trim(),
      category: TOPIC_2_TYPE.AI,
      ai_rule: {
        prompt: row.prompt?.trim() || '',
        prompt_en: '',
        filter: buildKeywordFilter(
          row.include,
          row.exclude,
          FEEDBACK_COLUMNS.SNIPPET,
        ),
      },
      readyState: false,
      promptChanged: true,
      isError: false,
      isLoading: false,
    }));
}

/**
 * Rule Topic CSV Row를 Topic2CreateParams로 변환
 * Rule 토픽은 FEEDBACK_TEXT 필드를 사용
 */
export function parseRuleTopicCsv(
  rows: RuleTopicCsvRow[],
): Topic2CreateParams<TOPIC_2_TYPE.RULE>[] {
  return rows
    .filter((row) => row.title?.trim())
    .map((row) => {
      const filter = buildKeywordFilter(
        row.include,
        row.exclude,
        FEEDBACK_COLUMNS.FEEDBACK_TEXT,
      );

      return {
        name: row.title.trim(),
        category: TOPIC_2_TYPE.RULE,
        filter_rule: filter || {},
      };
    });
}
